import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import RefreshButton from '../components/RefreshButton';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { Zap, PauseCircle, PlayCircle, RotateCcw } from 'lucide-react';
import { cstTime, cstDateTime } from '../lib/time';
import useVisibilityAwareInterval from '../hooks/useVisibilityAwareInterval';

export default function Sim() {
  const [s, setSettings] = useState(null);
  const [m, setMetrics] = useState(null);
  const [curve, setCurve] = useState([]);
  const [events, setEvents] = useState([]);
  const [last, setLast] = useState(null);
  // P12: sim status pill + speed control + optimistic tick counter.
  const [status, setStatus] = useState('SYNCED');  // SYNCED | PROCESSING | RUNNING
  const [speed, setSpeed] = useState(1);            // 1x / 2x / 5x auto-tick
  const [pendingTicks, setPendingTicks] = useState(0);
  // Phase 14 §11: background sim worker mode + speed
  const [worker, setWorker] = useState(null); // {settings:{mode,speed,...}, worker:{running,...}}
  const abortRef = useRef(null);

  const loadWorker = async () => {
    try {
      const { data } = await api.get('/sim/worker/status');
      setWorker(data || null);
    } catch {}
  };

  const setWorkerMode = async (mode) => {
    await api.post('/sim/worker/config', { mode });
    loadWorker();
    toast.success(`Sim mode → ${mode}`);
  };

  const setWorkerSpeed = async (sp) => {
    await api.post('/sim/worker/config', { speed: sp });
    loadWorker();
  };

  const setRiskPct = async (pct) => {
    await api.post('/sim/worker/config', { risk_per_trade_pct: pct });
    loadWorker();
  };

  const refresh = async () => {
    // Cancel any in-flight refresh to avoid race conditions (P12 stability).
    if (abortRef.current) { try { abortRef.current.abort(); } catch {} }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const [sr, mr, er, evr] = await Promise.all([
        api.get('/sim/settings', { signal: ctrl.signal }),
        api.get('/sim/metrics',  { signal: ctrl.signal }),
        api.get('/sim/equity?limit=500', { signal: ctrl.signal }),
        api.get('/sim/events?limit=50',  { signal: ctrl.signal }),
      ]);
      setSettings(sr.data);
      setMetrics(mr.data);
      setCurve(er.data.curve || []);
      setEvents(evr.data.events || []);
      setLast(new Date().toISOString());
    } catch (e) {
      // Ignore aborts silently; log others
      if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
        /* keep last known good */
      }
    }
  };

  useEffect(() => { refresh(); loadWorker(); return () => { if (abortRef.current) try { abortRef.current.abort(); } catch {} }; }, []);
  // Sim state every 30s (slower for readability; sim is not tick-critical)
  useVisibilityAwareInterval(() => { refresh(); loadWorker(); }, 30000, []);

  // P12: auto-tick engine — respects the speed control (1x=manual only,
  // 2x = tick every 30s, 5x = tick every 10s).
  useEffect(() => {
    if (!s?.enabled || speed === 1) return;
    const ms = speed === 5 ? 10000 : 30000;
    const iv = setInterval(() => { tickAsync(); }, ms);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.enabled, speed]);

  const toggle = async () => {
    const next = !s?.enabled;
    await api.post('/sim/settings', { enabled: next });
    toast.success(next ? 'LIVE SIMULATION ACTIVATED' : 'Simulation paused');
    refresh();
  };

  const tick = async () => {
    await tickAsync();
  };

  // P12: optimistic async tick — UI flips to PROCESSING instantly,
  // backend runs via job queue, UI syncs on completion.
  const tickAsync = async () => {
    setStatus('PROCESSING');
    setPendingTicks((n) => n + 1);
    try {
      const { data } = await api.post('/sim/tick/async');
      if (data?.result) {
        const r = data.result;
        if (r.opens?.length) toast.success(`${r.opens.length} SIM entries opened`);
        if (r.closes?.length) toast.success(`${r.closes.length} SIM exits closed`);
      } else if (data?.status === 'pending' || data?.status === 'running') {
        toast.message('Sim tick queued — syncing...');
      }
      await refresh();
      setStatus('SYNCED');
    } catch (e) {
      setStatus('SYNCED');
      toast.error('Sim tick failed');
    } finally {
      setPendingTicks((n) => Math.max(0, n - 1));
    }
  };

  const update = async (k, v) => {
    await api.post('/sim/settings', { [k]: v });
    refresh();
  };

  const reset = async () => {
    // Plain confirm — zero deps, works across every target. Destructive action.
    if (!window.confirm('Reset the simulator?\n\nThis will:\n  • close every open sim position\n  • wipe the equity curve\n  • clear the event log\n\nManual trades are NOT affected.')) return;
    try {
      const { data } = await api.post('/sim/reset');
      toast.success(`Reset — closed ${data.closed_sim_positions} pos, cleared ${data.cleared_equity_rows} equity rows`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sim reset failed');
    }
  };

  const chartData = curve.map((p) => ({
    t: cstTime(p.timestamp).replace(' CST', ''),
    equity: p.equity,
  }));

  const Stat = ({ label, value, color = '' }) => (
    <div className="panel p-3">
      <div className="text-[10px] tracking-[0.3em] uppercase font-display text-cyan-sys">{label}</div>
      <div className={`font-mono text-2xl mt-1 ${color}`}>{value}</div>
    </div>
  );

  const totalColor = (m?.total_return_pct || 0) >= 0 ? 'text-buy' : 'text-sell';

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <button
            data-testid="sim-toggle"
            onClick={toggle}
            className={`flex items-center gap-2 px-3 py-2 border text-[12px] font-display tracking-[0.25em] uppercase ${
              s?.enabled
                ? 'border-buy text-buy hover:bg-buy hover:text-black'
                : 'border-[var(--txt-muted)] text-[var(--txt-secondary)] hover:border-cyan-sys hover:text-cyan-sys'
            }`}
          >
            {s?.enabled ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
            {s?.enabled ? 'SIM ON — PAUSE' : 'START LIVE SIM'}
          </button>
          <button
            data-testid="sim-tick"
            onClick={tick}
            className="flex items-center gap-2 px-3 py-2 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black text-[12px] font-display tracking-[0.25em] uppercase"
          >
            <Zap size={14} /> Run Tick
          </button>
          <button
            data-testid="sim-reset"
            onClick={reset}
            title="Close all sim positions and wipe the equity / event log"
            className="flex items-center gap-2 px-3 py-2 border border-sell text-sell hover:bg-sell hover:text-black text-[12px] font-display tracking-[0.25em] uppercase"
          >
            <RotateCcw size={14} /> Reset
          </button>
          {/* P12: sim speed control */}
          <div className="flex items-center gap-1 ml-2" data-testid="sim-speed-control">
            <span className="text-[9px] uppercase tracking-[0.3em] text-cyan-sys mr-1">SPEED</span>
            {[1, 2, 5].map((x) => (
              <button
                key={x}
                data-testid={`sim-speed-${x}x`}
                onClick={() => setSpeed(x)}
                className={`px-2 py-1 text-[11px] font-display tracking-wider border ${
                  speed === x
                    ? 'bg-cyan-sys text-black border-cyan-sys'
                    : 'text-cyan-sys border-[var(--border-muted)] hover:border-cyan-sys'
                }`}
              >
                {x}x
              </button>
            ))}
          </div>
          {/* P12: sim status pill */}
          <span
            data-testid="sim-status-pill"
            className={`ml-2 text-[10px] tracking-[0.3em] uppercase px-2 py-1 border ${
              status === 'PROCESSING'
                ? 'text-hold border-hold animate-pulse'
                : pendingTicks > 0
                  ? 'text-cyan-sys border-cyan-sys'
                  : 'text-buy border-buy'
            }`}
          >
            {pendingTicks > 0 ? `PROCESSING (${pendingTicks})` : status}
          </span>
        </div>
        <RefreshButton onRefresh={refresh} lastUpdated={last} testId="sim-refresh" />
      </div>

      {/* Phase 14 §11: Background Sim Worker controls */}
      <div data-testid="sim-worker-panel" className="panel px-3 py-2 flex flex-wrap items-center gap-3 text-[11px] font-mono">
        <span className="text-[10px] uppercase tracking-[0.3em] text-cyan-sys">BG WORKER</span>
        <span className={`text-[10px] px-2 py-0.5 border ${worker?.worker?.running ? 'border-buy text-buy' : 'border-[var(--border-muted)] text-[var(--txt-muted)]'}`}>
          {worker?.worker?.running ? '● RUNNING' : '○ STOPPED'}
        </span>
        <div className="flex items-center gap-1">
          <span className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">mode</span>
          {['OFF', 'MANUAL', 'BACKGROUND'].map((mode) => (
            <button
              key={mode}
              data-testid={`sim-mode-${mode.toLowerCase()}`}
              onClick={() => setWorkerMode(mode)}
              className={`px-2 py-0.5 text-[10px] tracking-wider border ${
                worker?.settings?.mode === mode
                  ? 'bg-cyan-sys text-black border-cyan-sys'
                  : 'text-cyan-sys border-[var(--border-muted)] hover:border-cyan-sys'
              }`}
            >{mode}</button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">bg speed</span>
          {['1x', '2x', '5x'].map((sp) => (
            <button
              key={sp}
              data-testid={`sim-bg-speed-${sp}`}
              onClick={() => setWorkerSpeed(sp)}
              className={`px-2 py-0.5 text-[10px] border ${
                worker?.settings?.speed === sp
                  ? 'bg-cyan-sys text-black border-cyan-sys'
                  : 'text-cyan-sys border-[var(--border-muted)] hover:border-cyan-sys'
              }`}
            >{sp}</button>
          ))}
        </div>
        {/* Phase 14 §4: % risk input */}
        <div className="flex items-center gap-1" data-testid="sim-risk-pct">
          <span className="text-[9px] uppercase tracking-wider text-[var(--txt-muted)]">risk / trade</span>
          <input
            type="number"
            min="0.1"
            max="7"
            step="0.1"
            value={worker?.settings?.risk_per_trade_pct ?? 2}
            onChange={(e) => setRiskPct(parseFloat(e.target.value) || 2)}
            className="w-14 bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-1 py-0.5 text-[11px] font-mono"
          />
          <span className="text-[10px] text-cyan-sys">%</span>
          {(worker?.settings?.risk_per_trade_pct ?? 2) > (worker?.settings?.risk_pct_warn ?? 3) && (
            <span className="text-[9px] text-hold ml-1 uppercase">⚠ high</span>
          )}
        </div>
        <span className="text-[9px] text-[var(--txt-muted)] ml-auto uppercase tracking-wider">
          last tick {worker?.worker?.last_tick_age_s != null ? `${worker.worker.last_tick_age_s}s ago` : '—'}
          · trades/hr {worker?.worker?.trades_last_hour ?? 0}/{worker?.worker?.max_per_hour ?? 20}
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Current Equity" value={`$${(m?.current_equity ?? 0).toLocaleString()}`} color="text-cyan-sys" />
        <Stat label="Total Return" value={`${m?.total_return_pct ?? 0}%`} color={totalColor} />
        <Stat label="Max Drawdown" value={`${m?.max_drawdown_pct ?? 0}%`} color="text-sell" />
        <Stat label="Sharpe-like" value={`${m?.sharpe_like ?? 0}`} color="text-hold" />
        <Stat label="Trades" value={`${m?.trade_count ?? 0}`} />
        <Stat label="Win Rate" value={`${m?.win_rate ?? 0}%`} color="text-buy" />
        <Stat label="Avg Win" value={`$${m?.avg_win ?? 0}`} color="text-buy" />
        <Stat label="Avg Loss" value={`$${m?.avg_loss ?? 0}`} color="text-sell" />
      </div>

      <CornerPanel title="Simulation Equity Curve" active testId="sim-equity-panel">
        <div className="h-[260px] border border-[var(--border-default)] bg-black p-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <XAxis dataKey="t" stroke="#555" fontSize={10} />
              <YAxis stroke="#555" fontSize={10} domain={['dataMin', 'dataMax']} />
              <Tooltip
                contentStyle={{ background: '#0A0A0A', border: '1px solid #333', fontFamily: 'JetBrains Mono', fontSize: 11 }}
                labelStyle={{ color: '#08F7FE' }}
              />
              <Line type="monotone" dataKey="equity" stroke="#00FF41" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CornerPanel>

      <CornerPanel title="Simulation Settings" testId="sim-settings-panel">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px] font-mono">
          {[
            { k: 'starting_equity', label: 'Starting Equity ($)', type: 'number' },
            { k: 'risk_per_trade', label: 'Risk / Trade ($)', type: 'number' },
            { k: 'min_confluence', label: 'Min Confluence', type: 'number' },
          ].map((f) => (
            <label key={f.k}>
              <span className="block text-[10px] tracking-[0.25em] uppercase text-cyan-sys mb-1">{f.label}</span>
              <input
                data-testid={`sim-field-${f.k}`}
                type={f.type}
                defaultValue={s?.[f.k] ?? ''}
                onBlur={(e) => update(f.k, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                className="w-full bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-2 py-1.5"
              />
            </label>
          ))}
        </div>
      </CornerPanel>

      <CornerPanel title="Sim Event Feed" testId="sim-events-panel">
        <div className="max-h-[260px] overflow-y-auto text-[12px] font-mono space-y-1">
          {events.length === 0 && <div className="text-[var(--txt-muted)]">// no events yet</div>}
          {events.map((e, i) => (
            <div key={i} className="flex justify-between border-b border-[var(--border-default)] py-1">
              <span className={e.kind === 'open' ? 'text-cyan-sys' : e.pnl > 0 ? 'text-buy' : 'text-sell'}>
                ▸ {e.kind} {e.symbol} @ {e.price}
              </span>
              <span className="text-[var(--txt-secondary)] text-[10px]">
                {e.pnl != null ? `pnl=${e.pnl.toFixed ? e.pnl.toFixed(2) : e.pnl} · ` : ''}
                {cstDateTime(e.timestamp)}
              </span>
            </div>
          ))}
        </div>
      </CornerPanel>
    </div>
  );
}
